import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MicIcon from '@mui/icons-material/Mic';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MemoryIcon from '@mui/icons-material/Memory';
import BuildIcon from '@mui/icons-material/Build';
import ImageIcon from '@mui/icons-material/Image';
import LayersIcon from '@mui/icons-material/Layers';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PaletteIcon from '@mui/icons-material/Palette';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { THEME } from './genesisData';
import { InfoIcon, InfoIconFor } from '../../components/common/InfoIcon';
import Modal from '../../components/common/Modal';
import { relativeTime, absoluteTime } from '../../components/common/time';
import { AttachmentChip } from '../../components/common/attachments';
import { useGenesisRuns } from '../../hooks/useGenesisRuns';
import { getAuth } from 'firebase/auth';
import axios from 'axios';

// ----- T6 (mgmt-backend) wiring (Genesis intake, Chunk 1) -----
// Real ingest target for transcript uploads. Mirrors the proven pattern in
// EnterpriseAgentAccessTab.js:42,87-92 (REACT_APP_BACKEND_URL base + Firebase
// Bearer). The T1 read-only run list still flows through useGenesisRuns; only
// the upload path is wired to T6 in this chunk.
const T6_BASE = process.env.REACT_APP_BACKEND_URL || 'https://api.mgmt.joinunited.ai';

async function authHeader() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated. Please sign in again.');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// POST a transcript to T6. A file goes up as multipart (field `file`); pasted
// text goes up as JSON { source:'paste', text }. Returns the T6 response body
// { transcriptId, s3Key, charCount, status }. Throws on any non-2xx so the
// caller can surface the error (no silent fail).
export async function ingestTranscript({ file, text }) {
  const headers = await authHeader();
  if (file) {
    const form = new FormData();
    form.append('file', file);
    // Let the browser set the multipart boundary; do not force Content-Type.
    const res = await axios.post(`${T6_BASE}/api/admin/genesis/transcripts`, form, { headers });
    return res.data;
  }
  const res = await axios.post(
    `${T6_BASE}/api/admin/genesis/transcripts`,
    { source: 'paste', text },
    { headers }
  );
  return res.data;
}

// Extraction poll cadence (Chunk 2). Extraction takes ~45-50s; poll every 3s
// and give up after 90s so a stuck run surfaces as failed rather than hanging.
const POLL_INTERVAL_MS = 3000;
const POLL_CAP_MS = 90000;

// Best-effort human-readable message from a T6 axios error.
const t6ErrorMessage = (err) =>
  (err && err.response && err.response.data &&
    (err.response.data.detail || err.response.data.error || err.response.data.extractionError)) ||
  (err && err.message) ||
  'Request failed.';

const STAGES = [
  {
    key: 'discovery', n: 1, label: 'Discovery', layer: 'Layer 1', Icon: DescriptionIcon,
    done: 'Workflows and vendors extracted from transcript.',
    running: 'Reading transcript and extracting workflows...',
  },
  {
    key: 'pricing', n: 2, label: 'Pricing', layer: 'Layer 2', Icon: AttachMoneyIcon,
    done: 'Anchors computed from coordination cost.',
    running: 'Computing pricing anchors from coordination cost...',
  },
  {
    key: 'capabilities', n: 3, label: 'Capabilities', layer: 'Layer 3', Icon: MemoryIcon,
    done: 'Workflows matched to capability registry.',
    running: 'Matching workflows to capability registry...',
  },
  {
    key: 'buildCommitments', n: 4, label: 'Build Commitments', layer: 'Layer 4', Icon: BuildIcon,
    done: 'Planned capabilities converted to commitments.',
    running: 'Drafting build commitments...',
  },
  {
    key: 'visualAssets', n: 5, label: 'Visual Assets', layer: 'Layer 5', Icon: ImageIcon,
    done: 'Logo, brand colors, and references attached.',
    running: 'Collecting visual assets...',
  },
  {
    key: 'deck', n: 6, label: 'Deck Generation', layer: 'Output', Icon: LayersIcon,
    done: 'Slides assembled from validated layers.',
    running: 'Assembling deck...',
  },
  {
    key: 'loi', n: 7, label: 'LOI Generation', layer: 'Output', Icon: ArticleIcon,
    done: 'Letter of intent drafted.',
    running: 'Drafting letter of intent...',
  },
];

const DOT_COLORS = {
  high: THEME.mint,
  medium: THEME.amber,
  low: THEME.pink,
  live: THEME.mint,
  planned: THEME.amber,
  complete: THEME.mint,
  'in-progress': THEME.amber,
  pending: THEME.textMuted,
};

const TONE_MAP = {
  mint: { bg: THEME.mintDim, color: THEME.mint },
  amber: { bg: THEME.amberDim, color: THEME.amber },
  blue: { bg: THEME.blueDim, color: THEME.blue },
  muted: { bg: 'rgba(255,255,255,0.06)', color: THEME.textMuted },
};

const scrollSx = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(18,192,153,0.4)', borderRadius: '10px' },
};

const money = (n) => `$${Number(n).toLocaleString('en-US')}`;

const employeesLabel = (e) => (typeof e === 'number' ? `${e.toLocaleString('en-US')} employees` : e);

const outcomeTone = (o) => (o === 'pending' ? 'amber' : 'mint');

// Plain-English explainer copy shown in the info tooltips.
const TIP = {
  pipelineRuns: "Every transcript that's been processed through Genesis. Click to see how it became a deck.",
  discovery: 'Facts the AI pulled directly from the transcript. Workflows, vendors, customer shape.',
  pricing: 'How we landed on the Year-1 number. We take the MAX of three anchors so we never underprice.',
  max: 'This anchor was the highest of the three, so it sets the floor for Phase 3 monthly pricing.',
  coordination: 'Annual coordination labor cost times 0.80. Sanity check on the low end; we never price below 65% of this.',
  enterpriseFloor: "Minimum monthly we'd charge a serious enterprise account, regardless of how small their team is.",
  valueAnchor: "How much they'd otherwise pay to hire someone to do this work themselves. Our price stays below this.",
  capabilities: 'What the AI can do today, pulled live from the platform registry. Only LIVE items can be claimed in the deck.',
  buildCommitments: 'Things we promised to build specifically for this customer. Added by the salesperson, not the AI.',
  visualAssets: 'Customer logos, brand colors, and reference images uploaded by the salesperson.',
  live: 'Already shipped. Safe to claim in the deck as something we do today.',
  planned: 'On the roadmap but not built yet. Cannot be claimed as live; moves to Build Commitments instead.',
  confidence:
    'Mint = we captured frequency and volume from the call. Amber = partial data, estimated. Pink = neither captured, both inferred.',
  chatRail:
    'Genesis works with you here to refine the proposal. It asks clarifying questions about uploaded context; your answers sharpen the deck before it generates. Built to go both ways.',
  attachmentModel:
    'Files you attach are stored separately (not kept in the conversation memory) so the chat stays fast no matter how many you add. Genesis reads them only when it needs to answer something.',
};

// Dummy Genesis reply pool. Cycled deterministically by messageCount; no
// LLM call. Mix of acknowledgments and clarifying questions. Every
// targetField path resolves to a real field on the unified Run shape.
const DUMMY_GENESIS_REPLIES = [
  { type: 'message', text: 'Got it. Updating the proposal draft now.', targetField: null },
  { type: 'message', text: 'Noted. That sharpens the Layer 1 extraction.', targetField: null },
  { type: 'question', text: 'Should orchestration fee stay at 15%, or did we discuss a custom rate?', targetField: 'outputs.pricing.orchestrationFee' },
  { type: 'message', text: 'Understood. Folding that into the next deck pass.', targetField: null },
  { type: 'question', text: 'Any vendors not on the list yet?', targetField: 'outputs.discovery.vendors' },
  { type: 'question', text: 'Which offices are in scope?', targetField: 'shape.offices' },
];

// ----- helper components -----

const Card = ({ children, sx = {}, raised = false }) => (
  <Box
    sx={{
      background: raised ? THEME.cardRaised : THEME.card,
      border: `1px solid ${THEME.border}`,
      borderRadius: '12px',
      p: 2.5,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Badge = ({ children, tone = 'muted' }) => {
  const t = TONE_MAP[tone] || TONE_MAP.muted;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: t.bg,
        color: t.color,
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        px: '8px',
        py: '3px',
        borderRadius: '4px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
};

const StatusDot = ({ status, size = 8 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: DOT_COLORS[status] || THEME.textMuted,
      flexShrink: 0,
    }}
  />
);

const SectionHeader = ({ n, label, sublabel, IconComponent, sourceTone, sourceLabel, labelTip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: THEME.mintDim,
        color: THEME.mint,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
    {IconComponent && <IconComponent sx={{ fontSize: 18, color: THEME.textSecondary }} />}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary, lineHeight: 1.2 }}>
          {label}
        </Typography>
        {labelTip && <InfoIcon text={labelTip} />}
      </Box>
      {sublabel && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{sublabel}</Typography>}
    </Box>
    {sourceLabel && (
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Badge tone={sourceTone}>{sourceLabel}</Badge>
        <InfoIconFor label={sourceLabel} />
      </Box>
    )}
  </Box>
);

const PhaseStat = ({ label, value, sub, highlight }) => (
  <Box
    sx={{
      flex: 1,
      p: 1.5,
      borderRadius: '10px',
      background: highlight ? THEME.mintFaint : 'rgba(255,255,255,0.02)',
      border: highlight ? `1px solid ${THEME.mint}` : `1px solid ${THEME.border}`,
      textAlign: 'center',
    }}
  >
    <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: highlight ? THEME.mint : THEME.textPrimary, my: 0.25 }}>
      {money(value)}
    </Typography>
    <Typography sx={{ fontSize: '10px', color: THEME.textFaint }}>{sub}</Typography>
  </Box>
);

const outlinedBtnSx = {
  color: THEME.textSecondary,
  borderColor: THEME.borderStrong,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  '&:hover': { borderColor: THEME.mint, color: THEME.mint, background: THEME.mintFaint },
};

const mintBtnSx = {
  background: THEME.mint,
  color: '#06281f',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  boxShadow: 'none',
  '&:hover': { background: '#0fa884', boxShadow: 'none' },
};

// ----- left-rail row -----

const RunRow = ({ run, active, onClick }) => {
  const inProgress = run.lifecycle === 'in-progress';
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        background: active ? 'rgba(255,255,255,0.04)' : 'transparent',
        border: 'none',
        borderLeft: active ? `2px solid ${THEME.mint}` : '2px solid transparent',
        px: 2,
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        '&:hover': { background: 'rgba(255,255,255,0.03)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: run.customerColor, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: THEME.textPrimary,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {run.customer}
        </Typography>
        <Badge tone={inProgress ? 'amber' : 'mint'}>{inProgress ? 'In Progress' : 'Shipped'}</Badge>
        <InfoIconFor label={inProgress ? 'In Progress' : 'Shipped'} />
      </Box>
      <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{run.team}</Typography>
      <Typography sx={{ fontSize: '11px', color: THEME.textFaint }}>
        {inProgress ? run.uploaded : run.date}
      </Typography>
    </Box>
  );
};

// ----- in-progress view -----

const InProgressView = ({ run, onView }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
      <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: run.customerColor, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>{run.customer}</Typography>
      <Badge tone="amber">In Progress</Badge>
      <InfoIconFor label="In Progress" />
    </Box>
    <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 2.5 }}>
      {run.team} . Led by {run.teamLead} . Uploaded {run.uploaded}
    </Typography>

    <Card raised sx={{ mb: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          background: THEME.mintDim,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AutoAwesomeIcon sx={{ color: THEME.mint, fontSize: 22 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}>Genesis is processing this transcript</Typography>
        <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5 }}>
          Discovery is complete and pricing is being computed now. The remaining stages run automatically as each
          upstream layer validates.
        </Typography>
      </Box>
    </Card>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {STAGES.map((stage) => {
        const status = run.stages[stage.key];
        const complete = status === 'complete';
        const active = status === 'in-progress';
        const caption = complete ? stage.done : active ? stage.running : 'Waiting on upstream layer';
        return (
          <Card key={stage.key} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: complete ? THEME.mintDim : active ? THEME.amberDim : 'rgba(255,255,255,0.04)',
              }}
            >
              {complete && <CheckCircleIcon sx={{ fontSize: 22, color: THEME.mint }} />}
              {active && (
                <AccessTimeIcon
                  sx={{
                    fontSize: 22,
                    color: THEME.amber,
                    animation: 'genesisPulse 1.6s ease-in-out infinite',
                    '@keyframes genesisPulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.35 },
                    },
                  }}
                />
              )}
              {!complete && !active && <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: THEME.textMuted }} />}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <stage.Icon sx={{ fontSize: 15, color: THEME.textMuted }} />
                <Typography sx={{ fontSize: '10px', color: THEME.textFaint, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {stage.layer}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{stage.label}</Typography>
              </Box>
              <Typography sx={{ fontSize: '12px', color: active ? THEME.amber : THEME.textMuted, mt: 0.25 }}>
                {caption}
              </Typography>
            </Box>

            {complete && (
              <Button
                variant="outlined"
                size="small"
                sx={outlinedBtnSx}
                onClick={() => onView && onView(stage.key)}
              >
                View
              </Button>
            )}
            {active && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '6px',
                  background: THEME.amberDim,
                  color: THEME.amber,
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: THEME.amber,
                    animation: 'genesisPulse 1.6s ease-in-out infinite',
                    '@keyframes genesisPulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.35 },
                    },
                  }}
                />
                Running
                <InfoIconFor label="Running" />
              </Box>
            )}
            {!complete && !active && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled
                  title="Available once this layer completes."
                  sx={{
                    ...outlinedBtnSx,
                    '&.Mui-disabled': { color: THEME.textFaint, borderColor: THEME.border },
                  }}
                >
                  View
                </Button>
                <InfoIconFor label="Queued" />
              </Box>
            )}
          </Card>
        );
      })}
    </Box>
  </Box>
);

// ----- completed-run layers -----

const Layer1Discovery = ({ pair }) => {
  const { transcript } = pair;
  const { discovery } = pair.outputs;
  return (
    <Card>
      <SectionHeader
        n={1}
        label="Discovery"
        sublabel="Extracted from transcript"
        IconComponent={DescriptionIcon}
        sourceTone="blue"
        sourceLabel="Extracted"
        labelTip={TIP.discovery}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Workflows
            </Typography>
            <InfoIcon text={TIP.confidence} />
          </Box>
          {discovery.workflows.map((w, i) => (
            <Box
              key={w.name}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                py: 1,
                borderBottom: i < discovery.workflows.length - 1 ? `1px solid ${THEME.border}` : 'none',
              }}
            >
              <Box sx={{ mt: '5px' }}>
                <StatusDot status={w.confidence} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', color: THEME.textPrimary }}>{w.name}</Typography>
                <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
                  {w.frequency} . {w.volume?.text || w.volume || ''}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: DOT_COLORS[w.confidence] || THEME.textMuted,
                  whiteSpace: 'nowrap',
                }}
              >
                {w.confidence}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: '0 0 240px', minWidth: 220 }}>
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
            Vendors
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {discovery.vendors.map((v) => (
              <Box
                key={v}
                sx={{
                  background: 'rgba(255,255,255,0.06)',
                  border: `1px solid ${THEME.border}`,
                  borderRadius: '6px',
                  px: 1,
                  py: 0.5,
                  fontSize: '11px',
                  color: THEME.textSecondary,
                }}
              >
                {v}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: THEME.textMuted }}>
            <DescriptionIcon sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {transcript.filename}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: THEME.textFaint, mt: 0.5 }}>
            {transcript.words.toLocaleString('en-US')} words . {transcript.confidence} confidence
          </Typography>
        </Box>
      </Box>

      {discovery.notes ? (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: '8px',
            background: THEME.amberDim,
            border: '1px solid rgba(251,191,36,0.3)',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
          }}
        >
          <WarningAmberIcon sx={{ color: THEME.amber, fontSize: 18, flexShrink: 0, mt: '1px' }} />
          <Typography sx={{ fontSize: '12px', color: THEME.amber, lineHeight: 1.5 }}>{discovery.notes}</Typography>
        </Box>
      ) : null}
    </Card>
  );
};

const Layer2Pricing = ({ pair }) => {
  const p = pair.outputs.pricing;
  const anchors = [
    { label: 'Coordination x 80%', value: p.phase3Anchor, tip: TIP.coordination },
    { label: 'Enterprise floor', value: p.enterpriseFloor, tip: TIP.enterpriseFloor },
    { label: 'Value anchor', value: p.valueAnchor, tip: TIP.valueAnchor },
  ];
  return (
    <Card>
      <SectionHeader
        n={2}
        label="Pricing"
        sublabel="Computed from coordination cost"
        IconComponent={AttachMoneyIcon}
        sourceTone="blue"
        sourceLabel="Computed"
        labelTip={TIP.pricing}
      />
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        {anchors.map((a) => {
          const isMax = a.value === p.maxOfThree;
          return (
            <Box
              key={a.label}
              sx={{
                flex: '1 1 150px',
                p: 2,
                borderRadius: '10px',
                background: isMax ? THEME.mintFaint : 'rgba(255,255,255,0.02)',
                border: isMax ? `1px solid ${THEME.mint}` : `1px solid ${THEME.border}`,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {a.label}
                  </Typography>
                  <InfoIcon text={a.tip} />
                </Box>
                {isMax && (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Badge tone="mint">Max</Badge>
                    <InfoIcon text={TIP.max} />
                  </Box>
                )}
              </Box>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: isMax ? THEME.mint : THEME.textPrimary }}>
                {money(a.value)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <PhaseStat label="Phase 1" value={p.phase1} sub="monthly" />
        <PhaseStat label="Phase 2" value={p.phase2} sub="monthly" />
        <PhaseStat label="Phase 3" value={p.phase3} sub="monthly" highlight />
        <PhaseStat label="Year 1" value={p.year1} sub="first year" />
      </Box>

      <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 2 }}>
        Alternative cost: {p.alternative} . Orchestration fee: {p.orchestrationFee} on third-party spend
      </Typography>
    </Card>
  );
};

const Layer3Capabilities = ({ pair }) => (
  <Card>
    <SectionHeader
      n={3}
      label="Capabilities"
      sublabel="Matched from registry"
      IconComponent={MemoryIcon}
      sourceTone="blue"
      sourceLabel="Registry"
      labelTip={TIP.capabilities}
    />
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
      {pair.outputs.capabilities.map((cap) => (
        <Box
          key={cap.name}
          sx={{
            p: 1.75,
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.02)',
            border: `1px solid ${THEME.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StatusDot status={cap.status} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, flex: 1, minWidth: 0 }}>{cap.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: cap.status === 'live' ? THEME.mint : THEME.amber,
                }}
              >
                {cap.status}
              </Typography>
              <InfoIcon text={cap.status === 'live' ? TIP.live : TIP.planned} />
            </Box>
          </Box>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>Used for: {cap.usedFor}</Typography>
        </Box>
      ))}
    </Box>
    <Typography sx={{ fontSize: '11px', color: THEME.textMuted, fontStyle: 'italic', mt: 2, lineHeight: 1.5 }}>
      Only capabilities marked LIVE can be claimed in present tense in the deck. PLANNED capabilities move to Layer 4 as
      build commitments.
    </Typography>
  </Card>
);

const Layer4BuildCommits = ({ pair }) => (
  <Card>
    <SectionHeader
      n={4}
      label="Build Commitments"
      sublabel="Human-added"
      IconComponent={BuildIcon}
      sourceTone="amber"
      sourceLabel="Human"
      labelTip={TIP.buildCommitments}
    />
    <Box>
      {pair.outputs.buildCommitments.map((c, i) => (
        <Box
          key={c}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.25,
            borderBottom: i < pair.outputs.buildCommitments.length - 1 ? `1px solid ${THEME.border}` : 'none',
          }}
        >
          <BuildIcon sx={{ fontSize: 16, color: THEME.amber, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '13px', flex: 1, minWidth: 0 }}>{c}</Typography>
          <Badge tone="muted">user-added</Badge>
          <InfoIconFor label="user-added" />
        </Box>
      ))}
    </Box>
    <Box
      component="button"
      sx={{
        mt: 1.5,
        width: '100%',
        cursor: 'pointer',
        background: 'transparent',
        border: `1px dashed ${THEME.borderStrong}`,
        borderRadius: '8px',
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        color: THEME.textMuted,
        fontSize: '13px',
        fontWeight: 600,
        '&:hover': { borderColor: THEME.mint, color: THEME.mint },
      }}
    >
      <AddIcon sx={{ fontSize: 16 }} />
      Add commitment
    </Box>
  </Card>
);

const VisualSlot = ({ label, IconComponent, filled, swatch }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: '10px',
      border: `1px dashed ${filled ? THEME.mint : THEME.borderStrong}`,
      background: filled ? THEME.mintFaint : 'transparent',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1,
      textAlign: 'center',
    }}
  >
    {swatch ? (
      <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: swatch, border: `1px solid ${THEME.borderStrong}` }} />
    ) : (
      <IconComponent sx={{ fontSize: 22, color: THEME.textMuted }} />
    )}
    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: '11px', color: filled ? THEME.mint : THEME.textMuted }}>
      {filled ? 'Provided' : 'Click to upload'}
    </Typography>
  </Box>
);

const Layer5Visuals = ({ pair }) => (
  <Card>
    <SectionHeader
      n={5}
      label="Visual Assets"
      sublabel="Human-provided"
      IconComponent={ImageIcon}
      sourceTone="amber"
      sourceLabel="Human"
      labelTip={TIP.visualAssets}
    />
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
      <VisualSlot label="Customer logo" IconComponent={ImageIcon} />
      <VisualSlot label="Brand colors" IconComponent={PaletteIcon} filled swatch={pair.customerColor} />
      <VisualSlot label="Reference inspiration" IconComponent={PhotoLibraryIcon} />
    </Box>
  </Card>
);

const FinalDeck = ({ pair, onPreview, onDownload, onLoi }) => (
  <Box
    sx={{
      background: THEME.cardRaised,
      border: `1px solid ${THEME.mint}`,
      borderRadius: '12px',
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexWrap: 'wrap',
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '10px',
        background: THEME.mintDim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <LayersIcon sx={{ color: THEME.mint, fontSize: 24 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 180 }}>
      <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Generated Deck</Typography>
      <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>
        {pair.outputs.deck.slides} slides . {pair.outputs.deck.filename}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button variant="contained" size="small" sx={mintBtnSx} onClick={onPreview}>
        Preview
      </Button>
      <Button variant="outlined" size="small" sx={outlinedBtnSx} onClick={onDownload}>
        Download
      </Button>
      <Button variant="outlined" size="small" sx={outlinedBtnSx} onClick={onLoi}>
        Generate LOI
      </Button>
    </Box>
  </Box>
);

const CompletedRunView = ({ pair, onPreview, onDownload, onLoi }) => {
  const { shape, outcome } = pair;
  // The layered showcase below is written against the fully-materialized fixture
  // shape (shape / outputs.* / outcome). Live runs from GET /api/genesis/runs are
  // NOT guaranteed to carry that detail payload, so dereferencing shape.industry
  // (and, further down, pair.outputs.capabilities.map, pair.outputs.deck.slides,
  // transcript.words.toLocaleString, etc.) black-screens the page for a completed
  // live run. When the payload is absent, render an honest "not synced" state
  // instead of crashing. shape is legitimately optional for live runs.
  if (!shape || !pair.outputs || !outcome) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: pair.customerColor || THEME.textMuted, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>{pair.customer || 'Run'}</Typography>
          <Badge tone="muted">Details unavailable</Badge>
        </Box>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>
          This run's detail payload has not synced yet.
          {pair.team ? ` ${pair.team}.` : ''}
          {pair.teamLead ? ` Led by ${pair.teamLead}.` : ''}
          {pair.date ? ` ${pair.date}` : ''}
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: pair.customerColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>{pair.customer}</Typography>
        <Badge tone="mint">Shipped</Badge>
        <InfoIconFor label="Shipped" />
        <Badge tone={outcomeTone(outcome.result)}>{outcome.result}</Badge>
        <InfoIconFor label={outcome.result} />
      </Box>
      <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 0.5 }}>
        {pair.team} . Led by {pair.teamLead} . {pair.date}
      </Typography>
      <Typography sx={{ fontSize: '11px', color: THEME.textFaint, mb: 2.5 }}>
        {shape.industry} . {shape.teams} {shape.teams === 1 ? 'team' : 'teams'} . {shape.offices} offices .{' '}
        {employeesLabel(shape.employees)}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Layer1Discovery pair={pair} />
        <Layer2Pricing pair={pair} />
        <Layer3Capabilities pair={pair} />
        <Layer4BuildCommits pair={pair} />
        <Layer5Visuals pair={pair} />
        <FinalDeck pair={pair} onPreview={onPreview} onDownload={onDownload} onLoi={onLoi} />
      </Box>

      {outcome.notes ? (
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, fontStyle: 'italic', mt: 2, lineHeight: 1.5 }}>
          {outcome.notes}
        </Typography>
      ) : null}
    </Box>
  );
};

// ----- pickers and modals -----

// Status copy for the staged-transcript chip. Replaces the old static
// "staged locally" line now that uploads really POST to T6.
const UPLOAD_STATUS_TEXT = {
  idle: 'ready to upload',
  uploading: 'uploading...',
  uploaded: 'uploaded',
  error: 'upload failed',
};

export const TranscriptPicker = ({ onChange, status = 'idle' }) => {
  const [stagedFile, setStagedFile] = useState(null);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [draggingOver, setDraggingOver] = useState(false);
  const inputRef = useRef(null);

  const setFile = (file) => {
    // Keep the real File so the caller can POST it as multipart to T6.
    const sf = file ? { name: file.name, sizeKB: Math.round(file.size / 1024), file } : null;
    setStagedFile(sf);
    if (onChange) onChange(sf);
  };

  const clearAll = () => {
    setStagedFile(null);
    setPasteText('');
    setPasteMode(false);
    if (onChange) onChange(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggingOver(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  if (stagedFile) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderRadius: '8px',
          background: THEME.mintFaint,
          border: `1px solid ${THEME.mint}`,
        }}
      >
        <DescriptionIcon sx={{ fontSize: 18, color: THEME.mint }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '12px', color: THEME.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {stagedFile.name}
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              color: status === 'error' ? THEME.pink : status === 'uploaded' ? THEME.mint : THEME.textMuted,
            }}
          >
            {stagedFile.sizeKB} KB . {UPLOAD_STATUS_TEXT[status] || UPLOAD_STATUS_TEXT.idle}
          </Typography>
        </Box>
        <Box
          component="button"
          onClick={clearAll}
          sx={{ background: 'none', border: 'none', cursor: 'pointer', color: THEME.textMuted, '&:hover': { color: THEME.pink } }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    );
  }

  if (pasteMode) {
    return (
      <Box>
        <TextField
          multiline
          fullWidth
          minRows={4}
          maxRows={8}
          placeholder="Paste transcript here..."
          value={pasteText}
          onChange={(e) => {
            setPasteText(e.target.value);
            if (onChange) {
              onChange(
                e.target.value
                  ? { name: 'pasted-text.txt', sizeKB: Math.max(1, Math.round(e.target.value.length / 1024)), text: e.target.value }
                  : null
              );
            }
          }}
          InputProps={{ sx: { fontSize: 12, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border } } }}
        />
        <Box
          component="button"
          onClick={clearAll}
          sx={{ background: 'none', border: 'none', cursor: 'pointer', color: THEME.textMuted, fontSize: '10px', mt: 0.5, p: 0 }}
        >
          Cancel paste
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept=".txt,.md,.pdf,.docx"
        onChange={(e) => setFile(e.target.files && e.target.files[0])}
        style={{ display: 'none' }}
      />
      <Box
        sx={{
          border: `1px dashed ${draggingOver ? THEME.mint : THEME.borderStrong}`,
          background: draggingOver ? THEME.mintFaint : 'transparent',
          borderRadius: '10px',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': { borderColor: THEME.mint },
        }}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDraggingOver(true);
        }}
        onDragLeave={() => setDraggingOver(false)}
        onDrop={handleDrop}
      >
        <UploadFileIcon sx={{ color: THEME.textMuted }} />
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>Drop transcript or click to choose</Typography>
        <Box
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            setPasteMode(true);
          }}
          sx={{ background: 'none', border: 'none', cursor: 'pointer', color: THEME.mint, fontSize: '10px', mt: 0.5, p: 0 }}
        >
          Paste text instead
        </Box>
      </Box>
    </Box>
  );
};

const LiveCoachModal = ({ open, onClose }) => (
  <Modal
    open={open}
    onClose={onClose}
    title="Live Coach"
    subtitle="Real-time discovery assistant"
    size="sm"
    footer={
      <Button variant="contained" sx={mintBtnSx} onClick={onClose}>
        Got it
      </Button>
    }
  >
    <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.7 }}>
      Live Coach listens in during a discovery call and surfaces missing-data prompts in real time:
      vendors that have not been named, workflows that look incomplete, and pricing anchors waiting on a
      number. It is a Genesis assistant for the human running the call.
    </Typography>
    <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 2 }}>
      Available soon. Today this button is a placeholder.
    </Typography>
  </Modal>
);

const NewRunModal = ({ open, onClose, onCreate }) => {
  const [customer, setCustomer] = useState('');
  const [team, setTeam] = useState('');
  const [teamLead, setTeamLead] = useState('');
  const [staged, setStaged] = useState(null);   // { name, sizeKB, file?, text? } from TranscriptPicker
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle | uploading | uploaded | error
  const [uploadError, setUploadError] = useState('');

  const resetForm = () => {
    setCustomer('');
    setTeam('');
    setTeamLead('');
    setStaged(null);
    setUploadStatus('idle');
    setUploadError('');
  };

  const submit = async () => {
    // No transcript attached: keep the original local-only mock behavior so the
    // demo "New Run" without a file still works (no T6 call).
    if (!staged || (!staged.file && !staged.text)) {
      const payload = { customer, team, teamLead, filename: staged ? staged.name : null, transcriptId: null };
      resetForm();
      onCreate(payload);
      return;
    }

    // Transcript attached: REAL ingest to T6. Capture the returned transcriptId.
    setUploadStatus('uploading');
    setUploadError('');
    try {
      const data = await ingestTranscript({ file: staged.file, text: staged.text });
      setUploadStatus('uploaded');
      const payload = {
        customer,
        team,
        teamLead,
        filename: staged.name,
        transcriptId: data.transcriptId,
        charCount: data.charCount,
        ingestStatus: data.status,
      };
      resetForm();
      onCreate(payload);
    } catch (err) {
      // Surface the failure; do not close, do not create a fake run.
      setUploadStatus('error');
      setUploadError(
        (err.response && err.response.data && (err.response.data.detail || err.response.data.error)) ||
        err.message ||
        'Upload failed.'
      );
    }
  };

  const fieldLabelSx = { fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 };
  const inputSx = { sx: { fontSize: 13, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border } } };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Run"
      subtitle="Start a Genesis pipeline run from a transcript"
      size="md"
      footer={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={mintBtnSx}
            onClick={submit}
            disabled={!customer.trim() || uploadStatus === 'uploading'}
          >
            {uploadStatus === 'uploading' ? 'Uploading...' : 'Create Run'}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography sx={fieldLabelSx}>Customer</Typography>
          <TextField
            fullWidth
            size="small"
            value={customer}
            onChange={(e) => setCustomer(e.target.value)}
            placeholder="e.g. Bristol-Myers Squibb"
            InputProps={inputSx}
          />
        </Box>
        <Box>
          <Typography sx={fieldLabelSx}>Team in scope</Typography>
          <TextField
            fullWidth
            size="small"
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            placeholder="e.g. Employee Experience"
            InputProps={inputSx}
          />
        </Box>
        <Box>
          <Typography sx={fieldLabelSx}>Team lead</Typography>
          <TextField
            fullWidth
            size="small"
            value={teamLead}
            onChange={(e) => setTeamLead(e.target.value)}
            placeholder="e.g. Marcus Chen"
            InputProps={inputSx}
          />
        </Box>
        <Box>
          <Typography sx={fieldLabelSx}>Transcript</Typography>
          <TranscriptPicker
            status={uploadStatus}
            onChange={(sf) => {
              setStaged(sf);
              if (!sf) {
                setUploadStatus('idle');
                setUploadError('');
              }
            }}
          />
        </Box>
        {uploadStatus === 'error' && (
          <Box
            sx={{
              p: 1.25,
              borderRadius: '8px',
              background: 'rgba(198,61,87,0.12)',
              border: `1px solid ${THEME.pink}`,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <WarningAmberIcon sx={{ color: THEME.pink, fontSize: 16, flexShrink: 0, mt: '1px' }} />
            <Typography sx={{ fontSize: '11px', color: THEME.pink, lineHeight: 1.5 }}>
              {uploadError || 'Upload failed.'}
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: '11px', color: THEME.textFaint }}>
          On create, an attached transcript is uploaded to the Genesis service and the run lands in the
          left rail at lifecycle in-progress. With no transcript attached, the run is a local-only mock.
        </Typography>
      </Box>
    </Modal>
  );
};

const StageOutputModal = ({ open, onClose, pair, stageKey }) => {
  const payload = pair && pair.outputs ? pair.outputs[stageKey] : null;
  const title = (STAGES.find((s) => s.key === stageKey) || {}).label || 'Stage output';
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={pair ? pair.customer : ''}
      size="lg"
      footer={
        <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>
          Close
        </Button>
      }
    >
      {!payload && (
        <Typography sx={{ fontSize: '13px', color: THEME.textMuted }}>No output for this stage yet.</Typography>
      )}
      {payload && stageKey === 'discovery' && (
        <Box>
          <Typography sx={{ fontSize: '10px', color: THEME.textFaint, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
            Workflows ({payload.workflows.length})
          </Typography>
          {payload.workflows.map((w) => (
            <Box key={w.name} sx={{ py: 1, borderBottom: `1px solid ${THEME.border}` }}>
              <Typography sx={{ fontSize: '13px' }}>{w.name}</Typography>
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
                {w.frequency} . {w.volume?.text || w.volume || ''} . confidence {w.confidence}
              </Typography>
            </Box>
          ))}
          <Typography sx={{ fontSize: '10px', color: THEME.textFaint, textTransform: 'uppercase', letterSpacing: '0.08em', mt: 2, mb: 1 }}>
            Vendors
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {payload.vendors.map((v) => (
              <Box
                key={v}
                sx={{
                  fontSize: '11px',
                  color: THEME.textSecondary,
                  background: 'rgba(255,255,255,0.06)',
                  border: `1px solid ${THEME.border}`,
                  borderRadius: '6px',
                  px: 1,
                  py: 0.5,
                }}
              >
                {v}
              </Box>
            ))}
          </Box>
          {payload.notes && (
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted, fontStyle: 'italic' }}>{payload.notes}</Typography>
          )}
        </Box>
      )}
      {payload && stageKey === 'pricing' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {Object.entries(payload).map(([k, v]) => (
            <Box key={k}>
              <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase' }}>{k}</Typography>
              <Typography sx={{ fontSize: '13px', color: THEME.textPrimary }}>
                {typeof v === 'number' ? `$${v.toLocaleString('en-US')}` : v}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {payload && stageKey === 'capabilities' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {payload.map((cap) => (
            <Box key={cap.name} sx={{ p: 1.5, borderRadius: '8px', border: `1px solid ${THEME.border}` }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{cap.name}</Typography>
              <Typography
                sx={{
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  color: cap.status === 'live' ? THEME.mint : THEME.amber,
                  fontWeight: 700,
                }}
              >
                {cap.status}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>Used for: {cap.usedFor}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {payload && stageKey === 'buildCommitments' && (
        <Box>
          {payload.map((c) => (
            <Box key={c} sx={{ display: 'flex', gap: 1, py: 1, borderBottom: `1px solid ${THEME.border}` }}>
              <BuildIcon sx={{ fontSize: 14, color: THEME.amber }} />
              <Typography sx={{ fontSize: '13px' }}>{c}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {payload && stageKey === 'visualAssets' && (
        <Box>
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, mb: 1, fontStyle: 'italic' }}>
            {payload.heroPrompt}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {payload.palette.map((c) => (
              <Box
                key={c}
                title={c}
                sx={{ width: 40, height: 40, borderRadius: '6px', background: c, border: `1px solid ${THEME.borderStrong}` }}
              />
            ))}
          </Box>
        </Box>
      )}
      {payload && stageKey === 'deck' && (
        <Typography sx={{ fontSize: '13px' }}>
          {payload.slides} slides . {payload.filename} . status: {payload.status}
        </Typography>
      )}
      {payload && stageKey === 'loi' && (
        <Box>
          <Typography sx={{ fontSize: '13px' }}>Status: {payload.status}</Typography>
          {payload.filename && <Typography sx={{ fontSize: '13px' }}>Filename: {payload.filename}</Typography>}
          {payload.signedAt && <Typography sx={{ fontSize: '13px' }}>Signed: {payload.signedAt}</Typography>}
        </Box>
      )}
    </Modal>
  );
};

const DeckPreviewModal = ({ open, onClose, pair }) => {
  const slides = pair && pair.outputs && pair.outputs.deck ? pair.outputs.deck.slides : 0;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Deck Preview"
      subtitle={pair ? pair.customer : ''}
      size="lg"
      footer={
        <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>
          Close
        </Button>
      }
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
        {Array.from({ length: slides }).map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'relative',
              aspectRatio: '16 / 9',
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${THEME.border}`,
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                left: 6,
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: pair ? pair.customerColor : THEME.mint,
              }}
            />
            <Typography sx={{ fontSize: '11px', color: THEME.textFaint }}>slide {i + 1}</Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 2 }}>
        Preview only. Nothing leaves the page.
      </Typography>
    </Modal>
  );
};

const DownloadModal = ({ open, onClose, pair }) => {
  const deck = pair && pair.outputs ? pair.outputs.deck : null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Download Deck"
      size="sm"
      footer={
        <Button variant="contained" sx={mintBtnSx} onClick={onClose}>
          OK
        </Button>
      }
    >
      <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
        Preview-only download placeholder. Nothing is written to disk.
      </Typography>
      {deck && (
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 1 }}>
          {deck.filename} . {deck.slides} slides
        </Typography>
      )}
    </Modal>
  );
};

const LoiModal = ({ open, onClose, pair }) => {
  const loi = pair && pair.outputs ? pair.outputs.loi : null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Letter of Intent"
      size="sm"
      footer={
        <Button variant="contained" sx={mintBtnSx} onClick={onClose}>
          OK
        </Button>
      }
    >
      {!loi ? (
        <Typography sx={{ fontSize: '13px', color: THEME.textSecondary }}>
          No LOI generated yet for this run. Would generate now in a real flow.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: '13px' }}>Status: {loi.status}</Typography>
          {loi.filename && <Typography sx={{ fontSize: '13px' }}>Filename: {loi.filename}</Typography>}
          {loi.signedAt && <Typography sx={{ fontSize: '13px' }}>Signed: {loi.signedAt}</Typography>}
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1 }}>
            Preview only. Nothing leaves the page.
          </Typography>
        </Box>
      )}
    </Modal>
  );
};

// ----- chat panel (right rail) -----

const ChatPanel = ({ run, onSendMessage, onDeleteAttachment, onClose }) => {
  const [input, setInput] = useState('');
  const [stagedAttachments, setStagedAttachments] = useState([]);
  const [draggingOver, setDraggingOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCountRef = useRef(0);
  const messageCount = run && run.chatThread && Array.isArray(run.chatThread.messages) ? run.chatThread.messages.length : 0;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messageCount]);

  if (!run) return null;
  const thread = run.chatThread || { messages: [], messageCount: 0, durationMinutes: 0 };
  const messages = Array.isArray(thread.messages) ? thread.messages : [];

  const ingestFiles = (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    const stamp = String(Date.now()).slice(-6);
    const newAtts = arr.map((file, i) => {
      const ext = (file.name.split('.').pop() || 'file').toLowerCase();
      return {
        id: `att-${stamp}-${i}`,
        filename: file.name,
        fileType: ext,
        sizeBytes: file.size,
        s3Key: `s3://genesis-uploads/dummy-${stamp}-${i}`,
        uploadedAt: new Date().toISOString(),
        status: 'uploaded',
      };
    });
    setStagedAttachments((prev) => [...prev, ...newAtts]);
  };

  const send = () => {
    const text = input.trim();
    if (!text && stagedAttachments.length === 0) return;
    onSendMessage(text, stagedAttachments);
    setInput('');
    setStagedAttachments([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const removeStaged = (id) => setStagedAttachments((prev) => prev.filter((a) => a.id !== id));

  const handleDragEnter = (e) => {
    if (!e.dataTransfer || !e.dataTransfer.types || !Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    dragCountRef.current += 1;
    setDraggingOver(true);
  };
  const handleDragOver = (e) => {
    if (e.dataTransfer && e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
    }
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCountRef.current = Math.max(0, dragCountRef.current - 1);
    if (dragCountRef.current === 0) setDraggingOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    dragCountRef.current = 0;
    setDraggingOver(false);
    if (e.dataTransfer && e.dataTransfer.files) ingestFiles(e.dataTransfer.files);
  };

  const confirmDelete = () => {
    if (deleteTarget && onDeleteAttachment) {
      onDeleteAttachment(deleteTarget.messageId, deleteTarget.attachmentId);
    }
    setDeleteTarget(null);
  };

  const targetAttachment = (() => {
    if (!deleteTarget) return null;
    const msg = messages.find((m) => m.id === deleteTarget.messageId);
    if (!msg || !Array.isArray(msg.attachments)) return null;
    return msg.attachments.find((a) => a.id === deleteTarget.attachmentId) || null;
  })();

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        width: 360,
        flexShrink: 0,
        background: THEME.bgDeep,
        borderLeft: `1px solid ${THEME.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* drag overlay */}
      {draggingOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: THEME.mintFaint,
            border: `2px dashed ${THEME.mint}`,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <Typography sx={{ fontSize: '14px', color: THEME.mint, fontWeight: 600 }}>Drop files to attach</Typography>
        </Box>
      )}

      {/* header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${THEME.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary }}>Refine with Genesis</Typography>
          <InfoIcon text={TIP.chatRail} />
        </Box>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: THEME.textMuted,
            '&:hover': { color: THEME.textPrimary },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      {/* meta line */}
      <Typography
        sx={{
          fontSize: '10px',
          color: THEME.textFaint,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${THEME.border}`,
          flexShrink: 0,
        }}
      >
        {thread.messageCount} messages . {thread.durationMinutes} min
      </Typography>

      {/* messages */}
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2, ...scrollSx }}>
        {messages.length === 0 ? (
          <Typography sx={{ fontSize: '12px', color: THEME.textFaint, fontStyle: 'italic' }}>
            No messages yet. Start the conversation below.
          </Typography>
        ) : (
          messages.map((m) => {
            const isGenesis = m.role === 'genesis';
            const tagText =
              m.type === 'question' ? 'CLARIFYING QUESTION' : m.type === 'answer' ? 'ANSWERED' : null;
            const tagTone = m.type === 'question' ? THEME.mint : m.type === 'answer' ? THEME.blue : null;
            const atts = Array.isArray(m.attachments) ? m.attachments : [];
            return (
              <Box
                key={m.id}
                sx={{
                  display: 'flex',
                  justifyContent: isGenesis ? 'flex-start' : 'flex-end',
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    background: isGenesis ? THEME.mintFaint : THEME.cardRaised,
                    borderLeft: isGenesis ? `2px solid ${THEME.mint}` : 'none',
                    border: isGenesis ? 'none' : `1px solid ${THEME.border}`,
                    borderRadius: '8px',
                    p: 1.25,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: isGenesis ? THEME.mint : THEME.textPrimary }}>
                      {isGenesis ? 'Genesis' : 'You'}
                    </Typography>
                    <Typography
                      title={absoluteTime(m.createdAt)}
                      sx={{ fontSize: '10px', color: THEME.textFaint, cursor: 'help' }}
                    >
                      {relativeTime(m.createdAt)}
                    </Typography>
                  </Box>
                  {tagText && (
                    <Typography
                      sx={{
                        fontSize: '9px',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: tagTone,
                        mb: 0.5,
                      }}
                    >
                      {tagText}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.5 }}>{m.text}</Typography>
                  {m.targetField && (
                    <Typography sx={{ fontSize: '10px', color: THEME.textFaint, mt: 0.5, fontStyle: 'italic' }}>
                      field: {m.targetField}
                    </Typography>
                  )}
                  {atts.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                      {atts.map((a) => (
                        <AttachmentChip
                          key={a.id}
                          attachment={a}
                          onDelete={() => setDeleteTarget({ messageId: m.id, attachmentId: a.id })}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* input zone */}
      <Box sx={{ borderTop: `1px solid ${THEME.border}`, p: 1.5, flexShrink: 0 }}>
        {/* staged attachment chips */}
        {stagedAttachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {stagedAttachments.map((a) => (
              <AttachmentChip key={a.id} attachment={a} onDelete={() => removeStaged(a.id)} />
            ))}
          </Box>
        )}

        {/* input row */}
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => {
              ingestFiles(e.target.files);
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            sx={{ ...outlinedBtnSx, minWidth: 0, p: '8px 10px' }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            title="Attach files"
          >
            <AttachFileIcon sx={{ fontSize: 16 }} />
          </Button>
          <InfoIcon text={TIP.attachmentModel} />
          <TextField
            multiline
            fullWidth
            minRows={1}
            maxRows={3}
            placeholder="Refine the proposal..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              sx: {
                fontSize: 12,
                color: THEME.textPrimary,
                '& fieldset': { borderColor: THEME.border },
                '&:hover fieldset': { borderColor: THEME.borderStrong },
              },
            }}
          />
          <Button
            variant="contained"
            sx={{ ...mintBtnSx, minWidth: 0, p: '8px 12px' }}
            disabled={!input.trim() && stagedAttachments.length === 0}
            onClick={send}
          >
            <SendIcon sx={{ fontSize: 16 }} />
          </Button>
        </Box>
      </Box>

      {/* delete confirm modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Remove attachment?"
        size="sm"
        footer={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="contained" sx={mintBtnSx} onClick={confirmDelete}>
              Remove
            </Button>
          </Box>
        }
      >
        <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
          {targetAttachment
            ? `Remove ${targetAttachment.filename} from this conversation? This cannot be undone.`
            : 'Remove this attachment from the conversation? This cannot be undone.'}
        </Typography>
      </Modal>
    </Box>
  );
};

// ----- page -----

// ════════════════════════════════════════════════════════════════════
// REAL-RUN VIEW (Chunk 2): extract -> poll -> review the proposed spec.
// Only runs created from a real T6 transcript (run.transcriptId set) take
// this path; mock runs keep the 7-stage fixture view, untouched.
//
// proposedSpec shape (T6 services/genesisExtractionService.js:59-103,167,
// 190-200): top-level { agentId, displayName, iconHint, tagline, description,
// initiatesHandoffs[], recommendation: { type:'new'|'extend', targetAgentId,
// reasoning } } and states[] where each state is
//   { name, tools: [ { name, status:'existing'|'net_new', description? } ], ... }
// Tools live PER STATE, so the review panel flattens + dedupes them; a tool is
// shown net_new if ANY occurrence across states is net_new.
// ════════════════════════════════════════════════════════════════════

const REAL_STATUS_LABEL = {
  ingested: 'Ingested',
  extracting: 'Extracting',
  extracted: 'Extracted',
  failed: 'Failed',
  deployed: 'Deployed',
  rejected: 'Rejected',
};

const collectSpecTools = (spec) => {
  const states = spec && Array.isArray(spec.states) ? spec.states : [];
  const byName = new Map();
  states.forEach((st) => {
    const tools = st && Array.isArray(st.tools) ? st.tools : [];
    tools.forEach((t) => {
      if (!t) return;
      const name = typeof t === 'string' ? t : (t.name || t.toolName || 'tool');
      const isNetNew = typeof t === 'object' && t.status === 'net_new';
      const description = (typeof t === 'object' && t.description) || '';
      const prev = byName.get(name);
      if (!prev) {
        byName.set(name, { name, status: isNetNew ? 'net_new' : 'existing', description });
      } else {
        byName.set(name, {
          name,
          status: prev.status === 'net_new' || isNetNew ? 'net_new' : 'existing',
          description: prev.description || description,
        });
      }
    });
  });
  return Array.from(byName.values());
};

const ReadyToExtractCard = ({ onExtract }) => (
  <Card raised sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: THEME.mintDim, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <AutoAwesomeIcon sx={{ color: THEME.mint, fontSize: 22 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}>Transcript ingested. Ready to extract.</Typography>
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5, mb: 1.5 }}>
        Run Genesis extraction to propose an agent spec from this transcript. Opus reads the transcript plus the
        live tool and agent catalogs, then proposes states, tools, and a new-vs-extend recommendation. Takes about
        45 to 50 seconds.
      </Typography>
      <Button variant="contained" startIcon={<AutoAwesomeIcon />} sx={mintBtnSx} onClick={onExtract}>
        Run Genesis Extraction
      </Button>
    </Box>
  </Card>
);

const ExtractingCard = () => (
  <Card raised sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: THEME.amberDim, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <AccessTimeIcon
        sx={{
          color: THEME.amber, fontSize: 22,
          animation: 'genesisPulse 1.6s ease-in-out infinite',
          '@keyframes genesisPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
        }}
      />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}>Genesis is extracting the agent spec</Typography>
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5 }}>
        Reading the transcript and mapping proposed tools against the live catalog. This usually takes 45 to 50
        seconds; the panel updates automatically when extraction finishes.
      </Typography>
      <Box sx={{ mt: 1.5, display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: '6px', background: THEME.amberDim, color: THEME.amber, fontSize: '12px', fontWeight: 600 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: THEME.amber, animation: 'genesisPulse 1.6s ease-in-out infinite', '@keyframes genesisPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } } }} />
        Extracting
      </Box>
    </Box>
  </Card>
);

const FailedCard = ({ error, onRetry }) => (
  <Card sx={{ border: `1px solid ${THEME.pink}` }}>
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <WarningAmberIcon sx={{ color: THEME.pink, fontSize: 20, flexShrink: 0, mt: '1px' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.pink, mb: 0.5 }}>Extraction failed</Typography>
        <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>
          {error || 'Genesis could not extract a spec from this transcript.'}
        </Typography>
        <Button variant="outlined" sx={{ ...outlinedBtnSx, mt: 1.5 }} onClick={onRetry}>
          Retry extraction
        </Button>
      </Box>
    </Box>
  </Card>
);

const overrideLinkSx = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: THEME.mint,
  fontSize: '11px',
  fontWeight: 600,
  p: 0,
  '&:hover': { textDecoration: 'underline' },
};

const InlineError = ({ children }) => (
  <Box sx={{ p: 1.25, borderRadius: '8px', background: 'rgba(198,61,87,0.12)', border: `1px solid ${THEME.pink}`, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
    <WarningAmberIcon sx={{ color: THEME.pink, fontSize: 16, flexShrink: 0, mt: '1px' }} />
    <Typography sx={{ fontSize: '11px', color: THEME.pink, lineHeight: 1.5 }}>{children}</Typography>
  </Box>
);

const SpecReviewPanel = ({ spec, overriddenTools, onReExtract, onOverrideTool, onApprove, onReject }) => {
  const tools = collectSpecTools(spec);
  const netNewCount = tools.filter((t) => t.status === 'net_new').length;
  const states = Array.isArray(spec.states) ? spec.states : [];
  const rec = spec.recommendation || null;
  const isExtend = rec && rec.type === 'extend';

  // Server-confirmed overrides (reflected via poll / refetch): toolName -> reason.
  const overrideMap = new Map((overriddenTools || []).map((o) => [o.toolName, o.reason]));
  const netNewTools = tools.filter((t) => t.status === 'net_new');
  const unresolvedCount = netNewTools.filter((t) => !overrideMap.has(t.name)).length;

  // override UI
  const [openOverride, setOpenOverride] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [overrideErr, setOverrideErr] = useState('');

  // approve UI
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [approveBusy, setApproveBusy] = useState(false);
  const [approveErr, setApproveErr] = useState('');
  const [unmetTools, setUnmetTools] = useState([]); // 409 list (tool name strings)

  // reject UI
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);
  const [rejectErr, setRejectErr] = useState('');

  const unmetSet = new Set(unmetTools);

  const openOverrideBox = (toolName) => {
    setOpenOverride(toolName);
    setOverrideReason('');
    setOverrideErr('');
  };

  const submitOverride = async (toolName) => {
    if (!overrideReason.trim()) return;
    setOverrideBusy(true);
    setOverrideErr('');
    const res = await onOverrideTool(toolName, overrideReason.trim());
    setOverrideBusy(false);
    if (res && res.ok) {
      setOpenOverride(null);
      setOverrideReason('');
      setUnmetTools((prev) => prev.filter((n) => n !== toolName)); // clear any 409 highlight
    } else {
      setOverrideErr((res && res.message) || 'Override failed.');
    }
  };

  const doApprove = async () => {
    setApproveBusy(true);
    setApproveErr('');
    setUnmetTools([]);
    const res = await onApprove();
    if (res && res.ok) return; // run flips to 'deployed' via refetch; this panel unmounts
    setApproveBusy(false);
    setConfirmOpen(false);
    if (res && res.code === 'unmet_tools') {
      // 409: NOT a generic error. List + highlight the un-overridden net_new tools.
      setUnmetTools(res.unmetTools || []);
    } else {
      setApproveErr((res && res.message) || 'Approve failed.');
    }
  };

  const doReject = async () => {
    setRejectBusy(true);
    setRejectErr('');
    const res = await onReject(rejectReason.trim());
    if (res && res.ok) return; // run flips to 'rejected' via refetch; panel unmounts
    setRejectBusy(false);
    setRejectErr((res && res.message) || 'Reject failed.');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* header: emoji + displayName + tagline + about */}
      <Card>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ fontSize: '34px', lineHeight: 1, flexShrink: 0 }}>{spec.iconHint || '🤖'}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                {spec.displayName || spec.agentId || 'Proposed agent'}
              </Typography>
              <Badge tone="mint">Proposed spec</Badge>
            </Box>
            {spec.tagline && <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, mt: 0.25 }}>{spec.tagline}</Typography>}
            {spec.agentId && <Typography sx={{ fontSize: '11px', color: THEME.textFaint, mt: 0.25, fontFamily: 'monospace' }}>agentId: {spec.agentId}</Typography>}
          </Box>
        </Box>
        {spec.description && (
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6, mt: 1.5 }}>
            {spec.description}
          </Typography>
        )}
      </Card>

      {/* recommendation banner: the new-vs-extend insight */}
      {rec && (
        <Box
          sx={{
            p: 2,
            borderRadius: '12px',
            background: isExtend ? THEME.amberDim : THEME.mintFaint,
            border: `1px solid ${isExtend ? THEME.amber : THEME.mint}`,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
          }}
        >
          {isExtend
            ? <BuildIcon sx={{ color: THEME.amber, fontSize: 20, flexShrink: 0, mt: '1px' }} />
            : <AutoAwesomeIcon sx={{ color: THEME.mint, fontSize: 20, flexShrink: 0, mt: '1px' }} />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isExtend ? THEME.amber : THEME.mint, mb: 0.25 }}>
              {isExtend
                ? `Recommended: extend existing agent ${rec.targetAgentId || '(unspecified)'}`
                : 'Recommended: new agent'}
            </Typography>
            {rec.reasoning && (
              <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>{rec.reasoning}</Typography>
            )}
            <Typography sx={{ fontSize: '10px', color: THEME.textFaint, mt: 0.5, fontStyle: 'italic' }}>
              Advisory only. A complete standalone spec is still proposed below.
            </Typography>
          </Box>
        </Box>
      )}

      {/* tools: existing (neutral) vs net_new (highlighted) */}
      <Card>
        <SectionHeader n="T" label="Tools" sublabel={`${tools.length} total . ${netNewCount} net new`} IconComponent={BuildIcon} />
        {tools.length === 0 ? (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted, fontStyle: 'italic' }}>No tools referenced by this spec.</Typography>
        ) : (
          tools.map((t, i) => {
            const netNew = t.status === 'net_new';
            const isOverridden = netNew && overrideMap.has(t.name);
            const isUnmet = netNew && unmetSet.has(t.name);
            return (
              <Box
                key={`${t.name}-${i}`}
                sx={{
                  py: 1,
                  borderBottom: i < tools.length - 1 ? `1px solid ${THEME.border}` : 'none',
                  ...(isUnmet ? { background: 'rgba(198,61,87,0.08)', borderRadius: '6px', px: 1 } : {}),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <BuildIcon sx={{ fontSize: 16, color: isOverridden ? THEME.mint : netNew ? THEME.amber : THEME.textMuted, flexShrink: 0, mt: '2px' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, fontFamily: 'monospace' }}>{t.name}</Typography>
                    {netNew && t.description && (
                      <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.25, lineHeight: 1.5 }}>{t.description}</Typography>
                    )}
                    {isOverridden && (
                      <Typography sx={{ fontSize: '11px', color: THEME.mint, mt: 0.25, lineHeight: 1.5 }}>
                        overridden - {overrideMap.get(t.name)}
                      </Typography>
                    )}
                    {isUnmet && (
                      <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 0.25, lineHeight: 1.5 }}>
                        Must be overridden before deploy.
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                    <Badge tone={isOverridden ? 'mint' : netNew ? 'amber' : 'muted'}>
                      {isOverridden ? 'overridden' : netNew ? 'net new' : 'existing'}
                    </Badge>
                    {netNew && !isOverridden && openOverride !== t.name && (
                      <Box component="button" onClick={() => openOverrideBox(t.name)} sx={overrideLinkSx}>
                        Override
                      </Box>
                    )}
                  </Box>
                </Box>
                {netNew && openOverride === t.name && (
                  <Box sx={{ pl: '28px', pt: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      placeholder="Reason this net-new tool is acceptable to ship (required)"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      InputProps={{ sx: { fontSize: 12, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border } } }}
                    />
                    {overrideErr && (
                      <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 0.5 }}>{overrideErr}</Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                      <Button
                        variant="contained"
                        size="small"
                        sx={mintBtnSx}
                        disabled={!overrideReason.trim() || overrideBusy}
                        onClick={() => submitOverride(t.name)}
                      >
                        {overrideBusy ? 'Saving...' : 'Save override'}
                      </Button>
                      <Button variant="outlined" size="small" sx={outlinedBtnSx} disabled={overrideBusy} onClick={() => setOpenOverride(null)}>
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })
        )}
        {netNewCount > 0 && (
          <Typography sx={{ fontSize: '11px', color: unresolvedCount > 0 ? THEME.amber : THEME.mint, mt: 1.5, lineHeight: 1.5 }}>
            {netNewCount} net-new tool{netNewCount === 1 ? '' : 's'} . {unresolvedCount === 0 ? 'all overridden' : `${unresolvedCount} still need an override before deploy`}
          </Typography>
        )}
      </Card>

      {/* states: simple name list */}
      <Card>
        <SectionHeader n="S" label="States" sublabel={`${states.length} state${states.length === 1 ? '' : 's'}`} IconComponent={LayersIcon} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {states.length === 0 ? (
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted, fontStyle: 'italic' }}>No states defined.</Typography>
          ) : (
            states.map((st, i) => (
              <Box
                key={(st && st.name) || i}
                sx={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${THEME.border}`, borderRadius: '6px', px: 1, py: 0.5, fontSize: '11px', color: THEME.textSecondary, fontFamily: 'monospace' }}
              >
                {(st && st.name) || `state_${i + 1}`}
              </Box>
            ))
          )}
        </Box>
      </Card>

      {/* actions */}
      <Card>
        {unmetTools.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <InlineError>
              These tools must be overridden first: {unmetTools.join(', ')}. Add a reason to each above, then deploy again.
            </InlineError>
          </Box>
        )}
        {approveErr && (
          <Box sx={{ mb: 1.5 }}>
            <InlineError>{approveErr}</InlineError>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" sx={mintBtnSx} disabled={approveBusy} onClick={() => setConfirmOpen(true)}>
            {approveBusy ? 'Deploying...' : 'Approve & Deploy'}
          </Button>
          <Button
            variant="outlined"
            sx={{ ...outlinedBtnSx, borderColor: THEME.pink, color: THEME.pink, '&:hover': { borderColor: THEME.pink, color: THEME.pink, background: 'rgba(198,61,87,0.08)' } }}
            disabled={approveBusy}
            onClick={() => { setRejectReason(''); setRejectErr(''); setRejectOpen(true); }}
          >
            Reject
          </Button>
          <Button variant="outlined" sx={outlinedBtnSx} disabled={approveBusy} onClick={onReExtract}>
            Re-run extraction
          </Button>
        </Box>
        {unresolvedCount > 0 && (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1.25, lineHeight: 1.5 }}>
            Deploy is gated server-side: {unresolvedCount} net-new tool{unresolvedCount === 1 ? '' : 's'} still need an override.
          </Typography>
        )}
      </Card>

      {/* confirm deploy (real production deploy) */}
      <Modal
        open={confirmOpen}
        onClose={() => { if (!approveBusy) setConfirmOpen(false); }}
        title="Deploy to production?"
        subtitle={spec.displayName || spec.agentId || ''}
        size="sm"
        footer={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" sx={outlinedBtnSx} disabled={approveBusy} onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" sx={mintBtnSx} disabled={approveBusy} onClick={doApprove}>
              {approveBusy ? 'Deploying...' : 'Deploy now'}
            </Button>
          </Box>
        }
      >
        <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
          This approves and syncs {spec.displayName || spec.agentId || 'this agent'} as a live agent in production. The
          tool gate runs server-side; any un-overridden net-new tool will block the deploy and be listed for you to fix.
        </Typography>
      </Modal>

      {/* reject (optional reason) */}
      <Modal
        open={rejectOpen}
        onClose={() => { if (!rejectBusy) setRejectOpen(false); }}
        title="Reject this proposal?"
        subtitle={spec.displayName || spec.agentId || ''}
        size="sm"
        footer={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" sx={outlinedBtnSx} disabled={rejectBusy} onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" sx={mintBtnSx} disabled={rejectBusy} onClick={doReject}>
              {rejectBusy ? 'Rejecting...' : 'Reject'}
            </Button>
          </Box>
        }
      >
        <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6, mb: 1.5 }}>
          Rejecting marks this transcript rejected. No agent is deployed. An optional reason is recorded.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="Reason (optional)"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          InputProps={{ sx: { fontSize: 12, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border } } }}
        />
        {rejectErr && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 0.75 }}>{rejectErr}</Typography>}
      </Modal>
    </Box>
  );
};

const DeployedCard = ({ run }) => {
  const spec = run.proposedSpec || {};
  return (
    <Card sx={{ border: `1px solid ${THEME.mint}` }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <CheckCircleIcon sx={{ color: THEME.mint, fontSize: 22, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: THEME.mint, mb: 0.5 }}>Agent deployed</Typography>
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>
            {spec.displayName || spec.agentId || 'The agent'} was approved and synced to production. It now appears in
            the agent registry and can be enabled per org from Agent Access.
          </Typography>
          {spec.agentId && (
            <Typography sx={{ fontSize: '11px', color: THEME.textFaint, mt: 0.5, fontFamily: 'monospace' }}>agentId: {spec.agentId}</Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

const RejectedCard = ({ reason }) => (
  <Card>
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <CloseIcon sx={{ color: THEME.textMuted, fontSize: 20, flexShrink: 0, mt: '1px' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: THEME.textSecondary, mb: 0.5 }}>Proposal rejected</Typography>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6 }}>
          {reason ? `Reason: ${reason}` : 'This proposal was rejected. No agent was deployed.'}
        </Typography>
      </Box>
    </Box>
  </Card>
);

const RealRunView = ({ run, onExtract, onOverrideTool, onApprove, onReject }) => {
  const status = run.genesisStatus || run.ingestStatus || 'ingested';
  const spec = run.proposedSpec || null;

  let body;
  if (status === 'extracting') {
    body = <ExtractingCard />;
  } else if (status === 'failed') {
    body = <FailedCard error={run.extractionError} onRetry={onExtract} />;
  } else if (status === 'deployed') {
    body = <DeployedCard run={run} />;
  } else if (status === 'rejected') {
    body = <RejectedCard reason={run.rejectionReason} />;
  } else if (status === 'extracted' && spec) {
    body = (
      <SpecReviewPanel
        spec={spec}
        overriddenTools={run.overriddenTools}
        onReExtract={onExtract}
        onOverrideTool={onOverrideTool}
        onApprove={onApprove}
        onReject={onReject}
      />
    );
  } else {
    body = <ReadyToExtractCard onExtract={onExtract} />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: run.customerColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>{run.customer}</Typography>
        <Badge tone={status === 'extracted' || status === 'deployed' ? 'mint' : status === 'failed' || status === 'rejected' ? 'muted' : 'amber'}>
          {REAL_STATUS_LABEL[status] || status}
        </Badge>
      </Box>
      <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 2.5 }}>
        {run.team || 'No team set'} . Real Genesis run . transcript {run.transcriptId}
      </Typography>
      {body}
    </Box>
  );
};

const GenesisPage = () => {
  const { runs: fetched, loading } = useGenesisRuns();
  const [runs, setRuns] = useState(null);
  const [selectedRun, setSelectedRun] = useState('instacart-2026-04');
  const active = runs ? runs.find((r) => r.id === selectedRun) : null;
  const isInProgress = active && active.lifecycle === 'in-progress';
  // A "real run" came from a live T6 transcript (Chunk 1). It uses the
  // extract/poll/review path; mock runs (no transcriptId) keep the fixture view.
  const isRealRun = !!(active && active.transcriptId);

  const [liveCoachOpen, setLiveCoachOpen] = useState(false);
  const [newRunOpen, setNewRunOpen] = useState(false);
  const [stageViewKey, setStageViewKey] = useState(null);
  const [deckPreviewOpen, setDeckPreviewOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [loiOpen, setLoiOpen] = useState(false);

  const [chatOpen, setChatOpen] = useState(false);
  const pendingReplyRef = useRef(null);

  // Chunk 2: extraction polling. pollTimersRef maps transcriptId -> timeoutId so
  // a poll chain survives run-switches and is cleared on unmount. mountedRef
  // guards async writes after the component goes away.
  const pollTimersRef = useRef({});
  const mountedRef = useRef(true);

  // Seed runs once from the hook. If the static default selectedRun is
  // not present in the loaded set, pick the first one and align the chat
  // open default to its lifecycle (open on in-progress, closed otherwise).
  useEffect(() => {
    if (fetched && runs === null) {
      setRuns(fetched);
      const desired = fetched.find((r) => r.id === selectedRun) || fetched[0];
      if (desired) {
        if (desired.id !== selectedRun) setSelectedRun(desired.id);
        setChatOpen(desired.lifecycle === 'in-progress');
      }
    }
  }, [fetched, runs, selectedRun]);

  // Reset chat-open default when the selected run changes (open on
  // in-progress, closed on shipped). Also clear any pending dummy reply
  // so it does not land on the new run's thread.
  useEffect(() => {
    if (runs) {
      const r = runs.find((x) => x.id === selectedRun);
      if (r) setChatOpen(r.lifecycle === 'in-progress');
    }
    return () => {
      if (pendingReplyRef.current) {
        clearTimeout(pendingReplyRef.current);
        pendingReplyRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRun]);

  // Unmount cleanup (final safety).
  useEffect(
    () => () => {
      if (pendingReplyRef.current) {
        clearTimeout(pendingReplyRef.current);
        pendingReplyRef.current = null;
      }
    },
    []
  );

  const handleSendChat = (text, attachments) => {
    if (!active) return;
    const targetRunId = selectedRun;
    const repMsg = {
      id: `rep-${String(Date.now()).slice(-8)}`,
      role: 'rep',
      type: 'message',
      text,
      targetField: null,
      createdAt: new Date().toISOString(),
      attachments: Array.isArray(attachments) ? attachments : [],
    };

    setRuns((prev) =>
      prev.map((r) =>
        r.id === targetRunId
          ? {
              ...r,
              chatThread: {
                ...r.chatThread,
                messages: [...((r.chatThread && r.chatThread.messages) || []), repMsg],
                messageCount: ((r.chatThread && r.chatThread.messageCount) || 0) + 1,
              },
            }
          : r
      )
    );

    if (pendingReplyRef.current) clearTimeout(pendingReplyRef.current);
    pendingReplyRef.current = setTimeout(() => {
      pendingReplyRef.current = null;
      setRuns((prev) =>
        prev.map((r) => {
          if (r.id !== targetRunId) return r;
          const existing = (r.chatThread && r.chatThread.messages) || [];
          const count = (r.chatThread && r.chatThread.messageCount) || existing.length;
          const tpl = DUMMY_GENESIS_REPLIES[count % DUMMY_GENESIS_REPLIES.length];
          const genMsg = {
            id: `gen-${String(Date.now()).slice(-8)}`,
            role: 'genesis',
            type: tpl.type,
            text: tpl.text,
            targetField: tpl.targetField,
            createdAt: new Date().toISOString(),
            attachments: [],
          };
          return {
            ...r,
            chatThread: {
              ...r.chatThread,
              messages: [...existing, genMsg],
              messageCount: count + 1,
            },
          };
        })
      );
    }, 800);
  };

  const handleDeleteAttachment = (messageId, attachmentId) => {
    setRuns((prev) =>
      prev.map((r) => {
        if (r.id !== selectedRun) return r;
        const msgs = (r.chatThread && r.chatThread.messages) || [];
        return {
          ...r,
          chatThread: {
            ...r.chatThread,
            messages: msgs.map((m) =>
              m.id === messageId
                ? { ...m, attachments: (m.attachments || []).filter((a) => a.id !== attachmentId) }
                : m
            ),
          },
        };
      })
    );
  };

  // ─── Chunk 2: extract -> poll -> capture real spec ───

  // Clear poll timers on unmount (mountedRef stops any in-flight async writes).
  useEffect(() => () => {
    mountedRef.current = false;
    Object.values(pollTimersRef.current).forEach((id) => clearTimeout(id));
    pollTimersRef.current = {};
  }, []);

  const updateRun = (id, patch) => {
    setRuns((prev) => (prev ? prev.map((r) => (r.id === id ? { ...r, ...patch } : r)) : prev));
  };

  // Poll GET /transcripts/:id every 3s until extracted | failed (cap 90s).
  // Writes the real status + proposedSpec back onto the run, keyed by runId.
  const startPolling = (runId, transcriptId) => {
    if (pollTimersRef.current[transcriptId]) return; // already polling this one
    const begin = Date.now();
    const tick = async () => {
      if (!mountedRef.current) return;
      try {
        const headers = await authHeader();
        const res = await axios.get(`${T6_BASE}/api/admin/genesis/transcripts/${transcriptId}`, { headers });
        if (!mountedRef.current) return;
        const data = res.data || {};
        if (data.status === 'extracted') {
          updateRun(runId, {
            genesisStatus: 'extracted',
            proposedSpec: data.proposedSpec || null,
            editedSpec: data.editedSpec || null,
            overriddenTools: data.overriddenTools || [],
            extractionError: '',
          });
          delete pollTimersRef.current[transcriptId];
          return;
        }
        if (data.status === 'failed') {
          updateRun(runId, { genesisStatus: 'failed', extractionError: data.extractionError || 'Extraction failed.' });
          delete pollTimersRef.current[transcriptId];
          return;
        }
        if (Date.now() - begin > POLL_CAP_MS) {
          updateRun(runId, { genesisStatus: 'failed', extractionError: 'Extraction timed out after 90s. Re-run extraction.' });
          delete pollTimersRef.current[transcriptId];
          return;
        }
        pollTimersRef.current[transcriptId] = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (!mountedRef.current) return;
        // Transient errors keep polling within the cap; past the cap, fail.
        if (Date.now() - begin > POLL_CAP_MS) {
          updateRun(runId, { genesisStatus: 'failed', extractionError: t6ErrorMessage(err) });
          delete pollTimersRef.current[transcriptId];
          return;
        }
        pollTimersRef.current[transcriptId] = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    pollTimersRef.current[transcriptId] = setTimeout(tick, POLL_INTERVAL_MS);
  };

  const handleExtract = async (run) => {
    if (!run || !run.transcriptId) return;
    updateRun(run.id, { genesisStatus: 'extracting', extractionError: '' });
    try {
      const headers = await authHeader();
      // 202 Accepted; the real work happens async on T6, so we poll.
      await axios.post(`${T6_BASE}/api/admin/genesis/transcripts/${run.transcriptId}/extract`, {}, { headers });
      startPolling(run.id, run.transcriptId);
    } catch (err) {
      updateRun(run.id, { genesisStatus: 'failed', extractionError: t6ErrorMessage(err) });
    }
  };

  // Re-read the transcript from T6 so the panel reflects server truth
  // (status deployed/rejected, overriddenTools, deployResult) after an action.
  const refetchTranscript = async (run) => {
    try {
      const headers = await authHeader();
      const res = await axios.get(`${T6_BASE}/api/admin/genesis/transcripts/${run.transcriptId}`, { headers });
      if (!mountedRef.current) return;
      const d = res.data || {};
      updateRun(run.id, {
        genesisStatus: d.status || run.genesisStatus,
        proposedSpec: d.proposedSpec || run.proposedSpec || null,
        editedSpec: d.editedSpec || null,
        overriddenTools: d.overriddenTools || [],
        extractionError: d.extractionError || '',
        rejectionReason: d.rejectionReason || '',
        deployResult: d.deployResult || run.deployResult || null,
      });
    } catch (err) {
      // Non-fatal: keep optimistic state if the refetch fails.
    }
  };

  // Override a single net_new tool with a REQUIRED reason. Returns the new
  // authoritative overriddenTools so the panel reflects it immediately.
  const handleOverrideTool = async (run, toolName, reason) => {
    try {
      const headers = await authHeader();
      const res = await axios.post(
        `${T6_BASE}/api/admin/genesis/transcripts/${run.transcriptId}/override-tool`,
        { toolName, reason },
        { headers }
      );
      updateRun(run.id, { overriddenTools: (res.data && res.data.overriddenTools) || [] });
      return { ok: true };
    } catch (err) {
      return { ok: false, message: t6ErrorMessage(err) };
    }
  };

  // Approve + deploy. The 409 unmet_tools response is handled DISTINCTLY: it is
  // returned with the list of un-overridden net_new tools so the panel can list
  // and highlight them rather than showing a generic error.
  const handleApprove = async (run) => {
    try {
      const headers = await authHeader();
      const res = await axios.post(`${T6_BASE}/api/admin/genesis/transcripts/${run.transcriptId}/approve`, {}, { headers });
      updateRun(run.id, { genesisStatus: 'deployed', deployResult: (res.data && (res.data.sync || res.data)) || null });
      await refetchTranscript(run); // reflect server status authoritatively
      return { ok: true, data: res.data };
    } catch (err) {
      const resp = err.response;
      if (resp && resp.status === 409 && resp.data && resp.data.error === 'unmet_tools') {
        return {
          ok: false,
          code: 'unmet_tools',
          unmetTools: resp.data.unmetTools || [],
          message: resp.data.detail || 'Some net-new tools must be overridden first.',
        };
      }
      return { ok: false, message: t6ErrorMessage(err) };
    }
  };

  const handleReject = async (run, reason) => {
    try {
      const headers = await authHeader();
      await axios.post(`${T6_BASE}/api/admin/genesis/transcripts/${run.transcriptId}/reject`, { reason: reason || '' }, { headers });
      updateRun(run.id, { genesisStatus: 'rejected', rejectionReason: reason || '' });
      await refetchTranscript(run);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: t6ErrorMessage(err) };
    }
  };

  const handleCreateRun = (data) => {
    const stamp = String(Date.now()).slice(-6);
    // Key the run by the REAL transcriptId from T6 when present (so Chunk 2's
    // extract/poll can address it); fall back to a local id for the no-transcript
    // mock path.
    const id = data.transcriptId || `run-${stamp}`;
    const newRun = {
      id,
      transcriptId: data.transcriptId || null,
      ingestStatus: data.transcriptId ? (data.ingestStatus || 'ingested') : 'mock',
      customer: data.customer || 'New Customer',
      team: data.team || '',
      teamLead: data.teamLead || '',
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      customerColor: THEME.mint,
      uploaded: 'just now',
      overallConfidence: 'medium',
      lifecycle: 'in-progress',
      shape: { teams: null, offices: null, employees: null, industry: null },
      transcript: { filename: data.filename || null, words: null, confidence: null, excerpt: null, charCount: data.charCount || null },
      customerLogo: { filename: null, width: null, height: null, mimeType: null, byteSize: null },
      chatThread: { messageCount: 0, durationMinutes: 0, messages: [] },
      outcome: { result: 'pending', engagementSignal: null, notes: 'Newly created; pipeline running.' },
      stages: {
        discovery: 'in-progress',
        pricing: 'pending',
        capabilities: 'pending',
        buildCommitments: 'pending',
        visualAssets: 'pending',
        deck: 'pending',
        loi: 'pending',
      },
      outputs: {
        discovery: null,
        pricing: null,
        capabilities: null,
        buildCommitments: null,
        visualAssets: null,
        deck: null,
        loi: null,
        agentLane: null,
      },
    };
    setRuns([newRun, ...runs]);
    setSelectedRun(newRun.id);
    setNewRunOpen(false);
  };

  if (loading || runs === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: THEME.bg,
          color: THEME.textMuted,
          fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
        }}
      >
        <Typography sx={{ fontSize: '13px' }}>Loading runs...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 0px)',
        background: THEME.bg,
        color: THEME.textPrimary,
        fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
      }}
    >
      {/* header bar */}
      <Box
        sx={{
          borderBottom: `1px solid ${THEME.border}`,
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Genesis Pipeline
          </Typography>
          <Typography variant="caption" sx={{ color: THEME.textMuted }}>
            Transcript to Discovery to Pricing to Capabilities to Build Commits to Deck to LOI
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
          <Button
            variant="outlined"
            startIcon={<ChatBubbleOutlineIcon />}
            sx={
              chatOpen
                ? { ...outlinedBtnSx, borderColor: THEME.mint, color: THEME.mint, background: THEME.mintFaint }
                : outlinedBtnSx
            }
            onClick={() => setChatOpen((o) => !o)}
          >
            Chat
          </Button>
          <Button variant="outlined" startIcon={<MicIcon />} sx={outlinedBtnSx} onClick={() => setLiveCoachOpen(true)}>
            Live Coach
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} sx={mintBtnSx} onClick={() => setNewRunOpen(true)}>
            New Run
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* left rail */}
        <Box
          sx={{
            width: 300,
            flexShrink: 0,
            borderRight: `1px solid ${THEME.border}`,
            background: THEME.bgDeep,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: `1px solid ${THEME.border}`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: THEME.textFaint }}>
                Pipeline Runs
              </Typography>
              <InfoIcon text={TIP.pipelineRuns} />
            </Box>
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{runs.length}</Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', ...scrollSx }}>
            {runs.map((r) => (
              <RunRow key={r.id} run={r} active={r.id === selectedRun} onClick={() => setSelectedRun(r.id)} />
            ))}
          </Box>

          <Box sx={{ p: 2, borderTop: `1px solid ${THEME.border}`, flexShrink: 0 }}>
            <TranscriptPicker />
          </Box>
        </Box>

        {/* main canvas */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, ...scrollSx }}>
          {active &&
            (isRealRun ? (
              <RealRunView
                run={active}
                onExtract={() => handleExtract(active)}
                onOverrideTool={(toolName, reason) => handleOverrideTool(active, toolName, reason)}
                onApprove={() => handleApprove(active)}
                onReject={(reason) => handleReject(active, reason)}
              />
            ) : isInProgress ? (
              <InProgressView run={active} onView={(key) => setStageViewKey(key)} />
            ) : (
              <CompletedRunView
                pair={active}
                onPreview={() => setDeckPreviewOpen(true)}
                onDownload={() => setDownloadOpen(true)}
                onLoi={() => setLoiOpen(true)}
              />
            ))}
        </Box>

        {/* chat rail (right, conditional). Keyed by active.id so the panel's
            internal state (staged attachments, input draft, drag overlay, delete
            confirm target) resets cleanly on run-switch. */}
        {chatOpen && active && (
          <ChatPanel
            key={active.id}
            run={active}
            onSendMessage={handleSendChat}
            onDeleteAttachment={handleDeleteAttachment}
            onClose={() => setChatOpen(false)}
          />
        )}
      </Box>

      <LiveCoachModal open={liveCoachOpen} onClose={() => setLiveCoachOpen(false)} />
      <NewRunModal open={newRunOpen} onClose={() => setNewRunOpen(false)} onCreate={handleCreateRun} />
      <StageOutputModal
        open={!!stageViewKey}
        onClose={() => setStageViewKey(null)}
        pair={active}
        stageKey={stageViewKey}
      />
      <DeckPreviewModal open={deckPreviewOpen} onClose={() => setDeckPreviewOpen(false)} pair={active} />
      <DownloadModal open={downloadOpen} onClose={() => setDownloadOpen(false)} pair={active} />
      <LoiModal open={loiOpen} onClose={() => setLoiOpen(false)} pair={active} />
    </Box>
  );
};

export default GenesisPage;
